/* ------------------------ Functional imports start ------------------------ */
import { type Channel, type Message } from './types'
import {
  APIChannelFromJSON,
  APIMessageFromJSON,
  APIAllInOneUserFromJSON, type APIAllInOneUser, type APIChannel
} from "../../../generated-types";
import LogTool from '../../../logger/logTools'
import {
  createUser,
  createUserPreferences,
  getPKfromSelf,
  parseNestedObject,
  parseNestedObjectList,
} from '../../../utils/functions'
import { type User } from '../../../utils/types'
/* ------------------------ Functional imports end -------------------------- */

/* -------------------------------------------------------------------------- */
/*                           Utility functions start                          */
/* -------------------------------------------------------------------------- */

export function createAuthor(authorResponse: any): User {
  const apiAuthor: APIAllInOneUser = APIAllInOneUserFromJSON(authorResponse)
  return {
    ...apiAuthor,
    key: getPKfromSelf(apiAuthor.self),
    preferences: apiAuthor.preferences ? createUserPreferences(apiAuthor.preferences) : createUserPreferences({}),
  }
}

export function createMessage(messageResponse: any): Message {
  const apiMessage = APIMessageFromJSON(messageResponse)
  return {
    ...apiMessage,
    key: getPKfromSelf(apiMessage.self),
    author:
      apiMessage.author === null || apiMessage.author === undefined
        ? undefined
        : {
            ...apiMessage.author,
            key: getPKfromSelf(apiMessage.author.self),
            preferences: createUserPreferences({}),
          },
    channel: parseNestedObject(apiMessage.channel, createChannel),
    isNewMessage: Boolean(apiMessage.isNewMessage),
  }
}

export function createChannel(channelResponse: any): Channel {
  const apiChannel = APIChannelFromJSON(channelResponse)
  
  // Use epoch (1970) as default if lastReadAt is undefined, so all messages are considered unread
  const defaultLastReadAt = new Date(0)
  const lastReadAtDate = apiChannel.lastReadAt ? new Date(apiChannel.lastReadAt) : defaultLastReadAt

  return {
    ...apiChannel,
    key: getPKfromSelf(apiChannel.self),
    creator: parseNestedObject<User>(apiChannel.creator, createUser),
    members: parseNestedObjectList<User>(apiChannel.members, createUser),
    messages: parseNestedObjectList(apiChannel.messages, createMessage),
    unreadMessagesCount: 0, // to calculate the unreadMessageCount we need to know who the logged in user is -> calculate this property in a component
    lastReadAt: lastReadAtDate,
    prevLastReadAt: lastReadAtDate,
    get newestMessage() {
      // this getter function will always return the last entry in the message property
      // of the channel that is not a system message. That is the newest message, as long
      // as the message property is sorted (which it always should be!)
      if (typeof this.messages !== 'string') {
        let reverseIndex = this.messages.length - 1
        while (reverseIndex >= 0) {
          if (this.messages[reverseIndex].author) {
            return this.messages[reverseIndex]
          } else {
            reverseIndex--
          }
        }
      }
      return undefined
    },
  }
}

export function inputToAPIChannelJSON(input: any): APIChannel {
  return {
    ...(input.name && { name: input.name }),
    ...(input.description && { description: input.description }),
    ...(input.members && { members: input.members }),
  }
}

export function inputToAPIMessageJSON(input: string): any {
  return {
    body: input,
  }
}

export function getUnreadMessageCount(channel: Channel, user: User): number {
  if (typeof channel.messages !== 'string') {
    const lastReadAt = channel.lastReadAt.getTime()
    let unreadMessageCount = 0
    let reverseIndex = channel.messages.length - 1
    // loop through all messages from newest to oldest and count the unread messages.
    while (reverseIndex >= 0) {
      const message = channel.messages[reverseIndex]
      let authorKey: string | undefined = undefined
      if (typeof message.author !== 'undefined') {
        // message.author === undefined for system messages!
        authorKey =
          typeof message.author === 'string' ? getPKfromSelf(message.author) : message.author.key
      }
      if (
        authorKey === user.key ||
        lastReadAt - channel.messages[reverseIndex].createdAt.getTime() > 0
      ) {
        // Stop the loop once a message is found that the user as seen already or is the author of!
        break
      }

      unreadMessageCount += 1
      reverseIndex -= 1
    }
    return unreadMessageCount
  }
  return 0
}
